import { useState } from "react";
import {
    collection,
    query,
    where,
    getDocs,
    doc,
    writeBatch,
    getFirestore,
} from "firebase/firestore";
import {
    format,
    startOfMonth,
    endOfMonth,
    eachDayOfInterval,
    getDay,
    isWithinInterval,
} from "date-fns";
import Swal from "sweetalert2";
import * as XLSX from "xlsx";
import firebaseApp from "../../../../firebase/credenciales";

const db = getFirestore(firebaseApp);

// Utility to chunk arrays for parallel processing
const chunkArray = (array, size) => {
    const chunked = [];
    let index = 0;
    while (index < array.length) {
        chunked.push(array.slice(index, index + size));
        index += size;
    }
    return chunked;
};

// Helper: Convert "HH:mm" to Date object on 1970-01-01
const parseTime = (timeStr) => {
    if (!timeStr) return null;
    return new Date(`1970-01-01T${timeStr}:00`);
};

export const useGeneradorOcurrencias = (usuario, empleados, onGeneracionCompleta) => {
    const [generandoOcurrencias, setGenerandoOcurrencias] = useState(false);
    const [progreso, setProgreso] = useState({
        actual: 0,
        total: 0,
        mensaje: "",
    });
    const [showGenerarModal, setShowGenerarModal] = useState(false);
    const [parametrosGeneracion, setParametrosGeneracion] = useState({
        periodo: "",
        procesarAsistencias: true,
        procesarAusencias: true,
        sobreescribir: false,
        fechaCorte: "",
        metodo: "automatico", // "automatico" | "excel"
    });

    // Fetch Holidays for Company
    const obtenerFeriados = async (rucEmpresa, año) => {
        const q = query(
            collection(db, `pl_feriados/${rucEmpresa}/feriados`),
            where("año", "==", año),
            where("activo", "==", true)
        );
        const snapshot = await getDocs(q);
        const feriados = new Set();
        snapshot.forEach(doc => {
            const data = doc.data();
            feriados.add(data.fecha); // formato: "YYYY-MM-DD"
        });
        return feriados;
    };

    // 1. Fetch Work Regimes
    const obtenerRegimenes = async (rucEmpresa) => {
        const q = query(collection(db, "pl_regimen_trabajo"), where("ruc", "==", rucEmpresa));
        const snapshot = await getDocs(q);
        const regimenes = {};
        snapshot.forEach(doc => {
            regimenes[doc.id] = doc.data();
        });
        return regimenes;
    };

    // 2. Fetch Absences for an Employee in Period
    const obtenerAusenciasEmpleado = async (dni, fechaInicio, fechaFin) => {
        const q = query(
            collection(db, `ausencias/${dni}/ausencias`),
            where("estado", "in", ["APROBADO", "FINALIZADO"])
        );

        const snapshot = await getDocs(q);
        const ausencias = [];

        snapshot.forEach(doc => {
            const data = doc.data();
            const inicio = data.fechaInicio?.seconds ? new Date(data.fechaInicio.seconds * 1000) : new Date(data.fechaInicio);
            const fin = data.fechaFin?.seconds ? new Date(data.fechaFin.seconds * 1000) : new Date(data.fechaFin);

            if (inicio <= fechaFin && fin >= fechaInicio) {
                ausencias.push({ ...data, inicio, fin, tipoOriginal: data.tipo });
            }
        });
        return ausencias;
    };

    // 3. Fetch Attendance for an Employee in Period
    const obtenerAsistenciasEmpleado = async (dni, rucEmpresa, fechaInicio, fechaFin) => {
        const fechaInicioStr = format(fechaInicio, "yyyy-MM-dd");
        const fechaFinStr = format(fechaFin, "yyyy-MM-dd");

        const q = query(
            collection(db, `asistencia/${dni}/registros`),
            where("ruc", "==", rucEmpresa),
            where("fechaJornada", ">=", fechaInicioStr),
            where("fechaJornada", "<=", fechaFinStr)
        );

        const snapshot = await getDocs(q);
        const asistencias = {};

        snapshot.forEach(doc => {
            const data = doc.data();
            if (data.estado === "valido" || data.validacion?.esValida === true) {
                const fecha = data.fechaJornada;
                if (!asistencias[fecha]) asistencias[fecha] = { ingresos: [], salidas: [] };
                if (data.tipo === "Ingreso") asistencias[fecha].ingresos.push(data);
                else if (data.tipo === "Salida") asistencias[fecha].salidas.push(data);
            }
        });
        return asistencias;
    };

    const procesarEmpleado = (empleado, diasDelMes, regimenes, ausencias, asistencias, feriados, rucEmpresa, periodo, fechaCorte) => {
        const ocurrenciasEmpleado = [];
        const regimen = empleado.regimen_trabajo_id ? regimenes[empleado.regimen_trabajo_id] : null;

        const diasLaborablesDefault = [1, 2, 3, 4, 5];
        const horaEntradaDefault = "08:30";
        const toleranciaDefault = 10;

        for (const dia of diasDelMes) {
            const fechaStr = format(dia, "yyyy-MM-dd");
            if (fechaCorte && fechaStr > fechaCorte) continue;

            // PRIORIDAD 1: Verificar si es feriado
            if (feriados.has(fechaStr)) {
                continue; // Es feriado, no generar ocurrencia
            }

            // PRIORIDAD 2: Verificar ausencias aprobadas
            const ausenciaDelDia = ausencias.find(aus => isWithinInterval(dia, { start: aus.inicio, end: aus.fin }));

            if (ausenciaDelDia) {
                let tipoOcurrencia = "licencia";
                let conGoce = true;
                const tipoNorm = (ausenciaDelDia.tipoOriginal || "").toLowerCase();

                if (tipoNorm.includes("vacaciones")) tipoOcurrencia = "vacaciones";
                else if (tipoNorm.includes("medico") || tipoNorm.includes("médico")) tipoOcurrencia = "descanso_medico";
                else if (tipoNorm.includes("permiso")) { tipoOcurrencia = "permiso"; conGoce = false; }

                ocurrenciasEmpleado.push({
                    ruc_empresa: rucEmpresa,
                    empleado_id: empleado.dni,
                    empleado_nombre: `${empleado.apellidos || ""} ${empleado.nombres || ""}`.trim(),
                    periodo: periodo,
                    fecha: fechaStr,
                    tipo: tipoOcurrencia,
                    estado: "aprobado",
                    justificado: true,
                    con_goce: conGoce,
                    dias_afectados: 1,
                    horas_afectadas: 8,
                    motivo: ausenciaDelDia.motivo || `Ausencia: ${ausenciaDelDia.tipoOriginal}`,
                    observaciones: `Generado desde Ausencias`,
                    fecha_creacion: new Date(),
                    creado_por: "sistema_automatico"
                });
                continue;
            }

            // PRIORIDAD 3: Verificar régimen laboral
            const diaSemana = getDay(dia);
            const diaIso = diaSemana === 0 ? 7 : diaSemana;
            let esLaborable = false;
            let horaEntrada = horaEntradaDefault;
            let tolerancia = toleranciaDefault;

            if (regimen) {
                if (regimen.dias_laborables && regimen.dias_laborables.includes(diaIso)) {
                    esLaborable = true;
                    horaEntrada = regimen.hora_inicio || horaEntradaDefault;
                    tolerancia = regimen.tolerancia_minutos || toleranciaDefault;
                }
            } else {
                if (diasLaborablesDefault.includes(diaIso)) esLaborable = true;
            }

            if (!esLaborable) continue;

            // PRIORIDAD 4: Verificar asistencia
            const registro = asistencias[fechaStr];

            if (!registro || registro.ingresos.length === 0) {
                ocurrenciasEmpleado.push({
                    ruc_empresa: rucEmpresa,
                    empleado_id: empleado.dni,
                    empleado_nombre: `${empleado.apellidos || ""} ${empleado.nombres || ""}`.trim(),
                    periodo: periodo,
                    fecha: fechaStr,
                    tipo: "falta",
                    estado: "detectado_automatico",
                    justificado: false,
                    con_goce: false,
                    dias_afectados: 1,
                    horas_afectadas: 8,
                    minutos_tardanza: 0,
                    motivo: "Falta Injustificada",
                    observaciones: "Sin marcación en día laborable",
                    fecha_creacion: new Date(),
                    creado_por: "sistema_automatico",
                });
            } else {
                const primerIngreso = registro.ingresos.sort((a, b) => a.fechaMarcacion > b.fechaMarcacion ? 1 : -1)[0];
                const horaMarcacion = parseTime(primerIngreso.fechaMarcacion);
                const horaLimite = parseTime(horaEntrada);
                const horaLimiteConTolerancia = new Date(horaLimite.getTime() + (tolerancia * 60000));

                if (horaMarcacion > horaLimiteConTolerancia) {
                    const diffMs = horaMarcacion - horaLimite;
                    const minutosTardanza = Math.floor(diffMs / 60000);

                    ocurrenciasEmpleado.push({
                        ruc_empresa: rucEmpresa,
                        empleado_id: empleado.dni,
                        empleado_nombre: `${empleado.apellidos || ""} ${empleado.nombres || ""}`.trim(),
                        periodo: periodo,
                        fecha: fechaStr,
                        tipo: "tardanza",
                        estado: "detectado_automatico",
                        justificado: false,
                        con_goce: true,
                        dias_afectados: 0,
                        horas_afectadas: 0,
                        minutos_tardanza: minutosTardanza,
                        motivo: "Llegada tardía",
                        observaciones: `Entrada: ${primerIngreso.fechaMarcacion}`,
                        fecha_creacion: new Date(),
                        creado_por: "sistema_automatico",
                    });
                }
            }
        }
        return ocurrenciasEmpleado;
    };

    // --- NUEVO: Procesamiento de Excel ---
    const procesarExcel = async (archivo, rucEmpresa, periodo) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = async (e) => {
                try {
                    const data = new Uint8Array(e.target.result);
                    const workbook = XLSX.read(data, { 
                        type: "array",
                        cellDates: true,
                        cellText: false 
                    });
                    const sheetName = workbook.SheetNames[0];
                    const worksheet = workbook.Sheets[sheetName];
                    const jsonData = XLSX.utils.sheet_to_json(worksheet, { defval: "" });

                    const getVal = (row, ...names) => {
                        const keys = Object.keys(row);
                        const match = keys.find(k => names.some(n => k.toLowerCase().trim() === n.toLowerCase().trim()));
                        return match !== undefined ? row[match] : undefined;
                    };

                    const cleanDNI = (val) => {
                        if (val === undefined || val === null) return "";
                        const s = val.toString().trim().replace(/[^0-9]/g, "");
                        return s.padStart(8, "0");
                    };

                    const formatDate = (val) => {
                        if (!val) return "";
                        if (val instanceof Date) {
                            return val.toISOString().split('T')[0];
                        }
                        const s = val.toString().trim();
                        if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.substring(0, 10);
                        return s;
                    };

                    const resultados = [];
                    const errors = [];

                    if (jsonData.length > 0) {
                        console.log("📊 [Excel] Columnas detectadas:", Object.keys(jsonData[0]));
                    }

                    jsonData.forEach((row, idx) => {
                        const currentRow = idx + 2;
                        const dniRaw = getVal(row, "DNI", "dni", "Documento", "IDENTIDAD");
                        const dni = cleanDNI(dniRaw);
                        const fechaRaw = getVal(row, "Fecha", "fecha", "DATE", "dia");
                        const fecha = formatDate(fechaRaw);

                        console.log(`🔍 [Fila ${currentRow}] dniRaw: "${dniRaw}", cleaned: "${dni}", fechaRaw: "${fechaRaw}", cleaned: "${fecha}"`);

                        if (!dni || !fecha) {
                            if (dniRaw || fechaRaw) {
                                console.warn(`⚠️ [Fila ${currentRow}] Salto: Datos incompletos (DNI: ${dni}, Fecha: ${fecha})`);
                                errors.push(`Fila ${currentRow}: Falta DNI o Fecha válida`);
                            }
                            return;
                        }

                        const rowPeriodo = fecha.substring(0, 7);
                        if (rowPeriodo !== periodo) {
                            console.warn(`⚠️ [Fila ${currentRow}] Salto: Periodo discrepante (Fecha ${fecha} no es de ${periodo})`);
                            errors.push(`Fila ${currentRow}: La fecha ${fecha} no pertenece al periodo ${periodo} seleccionado.`);
                            return;
                        }

                        // Buscar nombre empleado si existe en memoria
                        const emp = empleados.find(e => {
                            const empDni = cleanDNI(e.dni || e.id);
                            return empDni === dni;
                        });

                        if (!emp) {
                            console.warn(`❌ [Fila ${currentRow}] Empleado no encontrado para DNI "${dni}"`);
                        }

                        const empNombre = emp ? `${emp.apellidos || ""} ${emp.nombres || ""}`.trim() : "Desconocido (Excel)";

                        const pushOcurrencia = (tipo, dias, horas, mins, goce, justif) => {
                            if (!tipo) return;
                            const tipoNormalized = tipo.toLowerCase().trim();
                            const esHE = ["he_25", "he_35", "he_100"].includes(tipoNormalized);

                            let finalDias = dias;
                            let finalHoras = horas;

                            if (!esHE) {
                                if (finalDias > 0 && finalHoras === 0) finalHoras = finalDias * 8;
                                else if (finalHoras > 0 && finalDias === 0) finalDias = finalHoras / 8;
                            } else {
                                finalDias = 0;
                            }

                            console.log(`✅ [Fila ${currentRow}] Agregando ${tipoNormalized} para ${dni} (${finalDias} dias, ${finalHoras} horas)`);
                            
                            resultados.push({
                                ruc_empresa: rucEmpresa,
                                empleado_id: dni,
                                empleado_nombre: empNombre,
                                periodo: periodo,
                                fecha: fecha,
                                tipo: tipoNormalized,
                                estado: "manual_importado",
                                justificado: justif,
                                con_goce: goce,
                                dias_afectados: finalDias,
                                horas_afectadas: finalHoras,
                                minutos_tardanza: mins,
                                motivo: (getVal(row, "Motivo", "motivo", "glosa") || "Importado desde Excel").toString().substring(0, 100),
                                observaciones: (getVal(row, "Observaciones", "observacion", "obs") || "").toString().substring(0, 200),
                                fecha_creacion: new Date(),
                                creado_por: "importacion_excel"
                            });
                        }

                        // Lógica básica
                        const tipoExcel = getVal(row, "Tipo", "tipo", "incidencia");
                        if (tipoExcel) {
                            const tipo = tipoExcel.toString().toLowerCase().trim();
                            const rawQty = getVal(row, "Días/Horas", "Cantidad", "Valor");
                            let dias = parseFloat(getVal(row, "Días Afectados", "dias", "cantidad_dias")) || 0;
                            let horas = parseFloat(getVal(row, "Horas Afectadas", "horas", "cantidad_horas")) || 0;
                            
                            // Si no se encontraron valores en columnas específicas, intentar con la genérica
                            if (dias === 0 && horas === 0 && rawQty) {
                                const rawStr = rawQty.toString().toLowerCase();
                                const num = parseFloat(rawQty) || 0;
                                if (rawStr.includes("día") || rawStr.includes("dia")) {
                                    dias = num;
                                } else if (rawStr.includes("hr") || rawStr.includes("ora")) {
                                    horas = num;
                                } else {
                                    // Fallback
                                    const esHE = ["he_25", "he_35", "he_100"].includes(tipo);
                                    if (esHE) horas = num;
                                    else dias = num;
                                }
                            }

                            const mins = parseFloat(getVal(row, "Minutos Tardanza", "minutos", "tardanza_minutos")) || 0;
                            const goceRaw = getVal(row, "Con Goce", "goce", "con_goce");
                            const justifRaw = getVal(row, "Justificado", "justificado", "justifica");
                            
                            const goce = goceRaw === "Sí" || goceRaw === "SI" || goceRaw === true || goceRaw === "true";
                            const justif = justifRaw === "Sí" || justifRaw === "SI" || justifRaw === true || justifRaw === "true";
                            
                            // EVITAR DUPLICADOS: Si el tipo es HE, solo pusheamos si hay cantidad aquí.
                            // Si dice "he_25" pero las horas están en la columna "Horas 25%", 
                            // la función checkHE se encargará luego.
                            const esHE = ["he_25", "he_35", "he_100"].includes(tipo);
                            if (!esHE || (horas > 0 || dias > 0)) {
                                pushOcurrencia(tipo, dias, horas, mins, goce, justif);
                            } else {
                                console.log(`ℹ️ [Fila ${currentRow}] Ignorando tipo ${tipo} con cantidad 0 (se espera en columnas específicas)`);
                            }
                        }

                        // Columnas HE
                        const checkHE = (namesOriginal, type) => {
                            const valRaw = getVal(row, ...namesOriginal);
                            if (valRaw === undefined || valRaw === null) return;
                            const val = parseFloat(valRaw);
                            if (val > 0) pushOcurrencia(type, 0, val, 0, true, true);
                        };
                        
                        checkHE(["Horas 25%", "HE 25%", "25%", "he25"], "he_25");
                        checkHE(["Horas 35%", "HE 35%", "35%", "he35"], "he_35");
                        checkHE(["Horas 100%", "HE 100%", "100%", "he100"], "he_100");
                    });

                    if (errors.length > 0) {
                        console.warn("⚠️ Errores en procesamiento de Excel:", errors);
                    }

                    resolve(resultados);
                } catch (err) {
                    reject(err);
                }
            };
            reader.onerror = (err) => reject(err);
            reader.readAsArrayBuffer(archivo);
        });
    };


    const analizarOcurrencias = async (archivoExcel = null) => {
        try {
            setGenerandoOcurrencias(true);
            const { periodo, fechaCorte, metodo } = parametrosGeneracion;
            const rucEmpresa = usuario.ruc;

            if (!periodo) throw new Error("Debe seleccionar un período");

            setProgreso({ actual: 0, total: 100, mensaje: "Iniciando..." });

            if (metodo === "excel") {
                if (!archivoExcel) throw new Error("Debe seleccionar un archivo Excel");
                setProgreso({ actual: 50, total: 100, mensaje: "Leyendo archivo Excel..." });
                const resultadosExcel = await procesarExcel(archivoExcel, rucEmpresa, periodo);
                setGenerandoOcurrencias(false);
                return resultadosExcel;

            } else {
                // Metodo Automatico
                const [año, mes] = periodo.split("-");
                const fechaInicioMes = startOfMonth(new Date(parseInt(año), parseInt(mes) - 1));
                let fechaFinCalculo = fechaCorte
                    ? new Date(parseInt(fechaCorte.split("-")[0]), parseInt(fechaCorte.split("-")[1]) - 1, parseInt(fechaCorte.split("-")[2]))
                    : endOfMonth(fechaInicioMes);

                const diasDelMes = eachDayOfInterval({ start: fechaInicioMes, end: fechaFinCalculo });

                setProgreso({ actual: 0, total: empleados.length, mensaje: "Cargando feriados y regímenes..." });

                // Cargar feriados del año
                const feriados = await obtenerFeriados(rucEmpresa, parseInt(año));
                const regimesMap = await obtenerRegimenes(rucEmpresa);

                const CHUNK_SIZE = 5;
                const chunks = chunkArray(empleados, CHUNK_SIZE);
                let processedCount = 0;
                let todasLasOcurrencias = [];

                for (const chunk of chunks) {
                    const resultadosChunk = await Promise.all(chunk.map(async (empleado) => {
                        const [ausencias, asistencias] = await Promise.all([
                            obtenerAusenciasEmpleado(empleado.dni, fechaInicioMes, fechaFinCalculo),
                            obtenerAsistenciasEmpleado(empleado.dni, rucEmpresa, fechaInicioMes, fechaFinCalculo)
                        ]);
                        return procesarEmpleado(empleado, diasDelMes, regimesMap, ausencias, asistencias, feriados, rucEmpresa, periodo, fechaCorte);
                    }));

                    resultadosChunk.forEach(res => todasLasOcurrencias.push(...res));
                    processedCount += chunk.length;
                    setProgreso({ actual: processedCount, total: empleados.length, mensaje: `Analizando... (${processedCount}/${empleados.length})` });
                }

                setGenerandoOcurrencias(false);
                return todasLasOcurrencias;
            }

        } catch (error) {
            console.error("Error en análisis:", error);
            setGenerandoOcurrencias(false);
            Swal.fire({ icon: "error", title: "Error", text: error.message });
            return [];
        }
    };

    const guardarOcurrencias = async (listaOcurrencias) => {
        try {
            setGenerandoOcurrencias(true);
            setProgreso({ actual: 0, total: listaOcurrencias.length, mensaje: "Guardando en base de datos..." });

            const rucEmpresa = usuario.ruc;
            const periodo = parametrosGeneracion.periodo;

            // Delete old auto-generated occurrences first
            if (parametrosGeneracion.sobreescribir) {
                const deleteQuery = query(
                    collection(db, "pl_ocurrencias"),
                    where("ruc_empresa", "==", rucEmpresa),
                    where("periodo", "==", periodo),
                    where("creado_por", "==", parametrosGeneracion.metodo === "excel" ? "importacion_excel" : "sistema_automatico")
                );
                const deleteSnapshot = await getDocs(deleteQuery);
                const docsToDelete = deleteSnapshot.docs;
                const BATCH_Delete = 500;
                for (let i = 0; i < docsToDelete.length; i += BATCH_Delete) {
                    const batch = writeBatch(db);
                    docsToDelete.slice(i, i + BATCH_Delete).forEach(d => batch.delete(d.ref));
                    await batch.commit();
                }
            }

            const BATCH_Save = 500;
            for (let i = 0; i < listaOcurrencias.length; i += BATCH_Save) {
                const batch = writeBatch(db);
                const lote = listaOcurrencias.slice(i, i + BATCH_Save);
                lote.forEach(ocurrencia => {
                    const customId = `${ocurrencia.empleado_id}_${ocurrencia.periodo}_${ocurrencia.tipo}_${ocurrencia.fecha}`;
                    const docRef = doc(db, "pl_ocurrencias", customId);
                    batch.set(docRef, ocurrencia);
                });
                await batch.commit();
            }

            Swal.fire({
                icon: "success",
                title: "Exito",
                text: `Se guardaron ${listaOcurrencias.length} ocurrencias.`
            });

            setShowGenerarModal(false);
            if (onGeneracionCompleta) onGeneracionCompleta(periodo);

        } catch (error) {
            console.error("Error guardando:", error);
            Swal.fire({ icon: "error", title: "Error", text: error.message });
        } finally {
            setGenerandoOcurrencias(false);
        }
    };

    return {
        generandoOcurrencias,
        progreso,
        showGenerarModal,
        setShowGenerarModal,
        parametrosGeneracion,
        setParametrosGeneracion,
        analizarOcurrencias,
        guardarOcurrencias
    };
};
